import { isUuidV7 } from '@/i12e/utilities/uuidToolkit';

/**
 * @fileoverview Protocol v3 data shapes and validation helpers.
 *
 * These helpers are deliberately independent from Vue and Axios. The data
 * layer can later map them to HTTP while domain code keeps one protocol shape.
 */

export const SYNC_ENTITY_TYPES = Object.freeze(['task', 'work_interval']);
export const SYNC_OPERATIONS = Object.freeze(['create', 'update', 'delete']);
export const SYNC_RESULT_STATUSES = Object.freeze([
  'accepted',
  'rejected',
  'conflict'
]);
export const SYNC_ERROR_CODES = Object.freeze([
  'invalid_request',
  'unsupported_entity_type',
  'invalid_operation',
  'invalid_entity_id',
  'invalid_payload',
  'too_many_operations',
  'payload_too_large',
  'stale_base_version',
  'resync_required'
]);

export const SYNC_PROTOCOL_LIMITS = Object.freeze({
  max_ops_per_push: 100,
  max_push_payload_bytes: 524288,
  max_page_size: 500
});

const includes = (values, value) => values.includes(value);

/**
 * @typedef {Object} PullRequest
 * @property {number} last_seen_server_version
 * @property {number} page_size
 */

/**
 * @typedef {Object} PushResult
 * @property {string} idempotency_key
 * @property {'accepted'|'rejected'|'conflict'} status
 * @property {number|null} server_version
 * @property {string|null} reason
 */

/**
 * Checks pull query values before they are sent to the API adapter.
 *
 * @param {unknown} request - Candidate pull request.
 * @returns {boolean} Whether the request matches the protocol shape.
 */
export const isPullRequest = (request) =>
  Boolean(
    request &&
      Number.isInteger(request.last_seen_server_version) &&
      request.last_seen_server_version >= 0 &&
      Number.isInteger(request.page_size) &&
      request.page_size > 0 &&
      request.page_size <= SYNC_PROTOCOL_LIMITS.max_page_size
  );

/**
 * Checks the required shape of an authoritative change.
 *
 * Delete changes use a null payload because the server retains metadata but
 * does not retain deleted business data.
 *
 * @param {unknown} change - Candidate authoritative change.
 * @returns {boolean} Whether the value matches the protocol shape.
 */
export const isAuthoritativeChange = (change) =>
  Boolean(
    change &&
      Number.isInteger(change.server_version) &&
      change.server_version >= 0 &&
      includes(SYNC_ENTITY_TYPES, change.entity_type) &&
      isUuidV7(change.entity_id) &&
      includes(SYNC_OPERATIONS, change.op) &&
      (change.payload === null || typeof change.payload === 'object')
  );

/**
 * Checks a pull response before the domain applies authoritative state.
 *
 * @param {unknown} response - Candidate pull response.
 * @returns {boolean} Whether the response matches the protocol shape.
 */
export const isPullResponse = (response) =>
  Boolean(
    response &&
      typeof response.resync_required === 'boolean' &&
      Array.isArray(response.authoritative_changes) &&
      response.authoritative_changes.every(isAuthoritativeChange)
  );

/**
 * Checks a push operation before it reaches the transport layer.
 *
 * @param {unknown} operation - Candidate push operation.
 * @returns {boolean} Whether the operation matches the protocol shape.
 */
export const isPushOperation = (operation) =>
  Boolean(
    operation &&
      typeof operation.idempotency_key === 'string' &&
      operation.idempotency_key.length > 0 &&
      includes(SYNC_ENTITY_TYPES, operation.entity_type) &&
      isUuidV7(operation.entity_id) &&
      includes(SYNC_OPERATIONS, operation.op) &&
      Number.isInteger(operation.base_server_version) &&
      operation.base_server_version >= 0 &&
      (operation.payload === null || typeof operation.payload === 'object')
  );

/**
 * Checks a push request and enforces the documented operation-count limit.
 *
 * @param {unknown} request - Candidate push request.
 * @returns {boolean} Whether the request matches the protocol shape.
 */
export const isPushRequest = (request) =>
  Boolean(
    request &&
      Array.isArray(request.operations) &&
      request.operations.length <= SYNC_PROTOCOL_LIMITS.max_ops_per_push &&
      request.operations.every(isPushOperation)
  );

/**
 * Checks one result returned for a push operation.
 *
 * @param {unknown} result - Candidate push result.
 * @returns {boolean} Whether the result matches the protocol shape.
 */
export const isPushResult = (result) =>
  Boolean(
    result &&
      typeof result.idempotency_key === 'string' &&
      result.idempotency_key.length > 0 &&
      includes(SYNC_RESULT_STATUSES, result.status) &&
      (result.server_version === null ||
        (Number.isInteger(result.server_version) &&
          result.server_version >= 0)) &&
      (result.reason === null || includes(SYNC_ERROR_CODES, result.reason))
  );

/**
 * Checks the complete push response before its results are applied locally.
 *
 * @param {unknown} response - Candidate push response.
 * @returns {boolean} Whether the response matches the protocol shape.
 */
export const isPushResponse = (response) =>
  Boolean(
    response &&
      Array.isArray(response.results) &&
      response.results.every(isPushResult) &&
      Array.isArray(response.authoritative_changes) &&
      response.authoritative_changes.every(isAuthoritativeChange)
  );
